import { useEffect, useRef } from 'react';
import { tokens, Icon } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';

export interface CostConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  callDescription: string;
  costClass: 'expensive';
}

/**
 * CostConfirmDialog — GVW-171.
 * 
 * Per-call confirmation modal for expensive copilot operations. Shows current
 * usage meter and requires explicit confirmation before proceeding.
 */
export function CostConfirmDialog({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  callDescription, 
  costClass 
}: CostConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  
  // Mock cost budget state (replace with actual platform SDK integration)
  const mockCostBudgetState = useViewerStore((state) => state.mockCostBudgetState);

  // Focus management
  useEffect(() => {
    if (isOpen && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onCancel]);

  if (!isOpen || !mockCostBudgetState) return null;

  const { used, total, window: timeWindow } = mockCostBudgetState;
  const percentage = total > 0 ? (used / total) * 100 : 0;
  const severity = percentage >= 80 ? 'warning' : 'rest';

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: tokens.color.overlay.scrim,
          zIndex: tokens.zIndex.modal,
        }}
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div
        ref={dialogRef}
        data-gvw-id="GVW-171"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cost-confirm-title"
        aria-describedby="cost-confirm-description"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: tokens.layout.popoverLg * 1.33 + 'px',
          maxWidth: `calc(100vw - ${tokens.space[5] ?? 32}px)`,
          maxHeight: `calc(100vh - ${tokens.space[5] ?? 32}px)`,
          background: tokens.color.surface[2],
          borderRadius: tokens.radius.lg,
          border: tokens.border.thin,
          boxShadow: `0 ${tokens.space[2]} ${tokens.space[5]} ${tokens.color.overlay.scrim}`,
          zIndex: tokens.zIndex.modal + 1,
          fontFamily: tokens.font.family.ui,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
          padding: `${tokens.space[4]} ${tokens.space[4]} ${tokens.space[2]}`,
          borderBottom: tokens.border.thin,
        }}>
          <Icon name="payments" size={24} decorative />
          <h2
            id="cost-confirm-title"
            style={{
              margin: 0,
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.medium,
              color: tokens.color.ink.primary,
              flex: 1,
            }}
          >
            Confirm expensive operation
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close dialog"
            style={{
              minHeight: tokens.density.touchMin,
              width: tokens.density.touchMin,
              border: 'none',
              borderRadius: tokens.radius.md,
              background: 'transparent',
              color: tokens.color.ink.secondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="close" size={20} decorative />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: tokens.space[4],
        }}>
          <div
            id="cost-confirm-description"
            style={{
              marginBottom: tokens.space[4],
              fontSize: tokens.font.size.sm,
              color: tokens.color.ink.primary,
              lineHeight: '1.5',
            }}
          >
            <p style={{ margin: `0 0 ${tokens.space[3]}` }}>
              You are about to invoke: <strong>{callDescription}</strong>
            </p>
            <p style={{ margin: `0 0 ${tokens.space[3]}` }}>
              This is an <strong>{costClass}</strong> operation that will count towards your cost budget.
            </p>
            <p style={{ 
              margin: 0, 
              fontSize: tokens.font.size.xs,
              color: tokens.color.ink.secondary,
            }}>
              An audit row will be written for this call.
            </p>
          </div>

          {/* Current usage meter */}
          <div style={{
            padding: tokens.space[3],
            background: severity === 'warning' ? 'var(--md-warning-container)' : 'var(--md-surface-container)',
            color: severity === 'warning' ? 'var(--md-on-warning-container)' : tokens.color.ink.primary,
            borderRadius: tokens.radius.input,
            marginBottom: tokens.space[4],
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
              marginBottom: tokens.space[2],
            }}>
              <Icon 
                name={severity === 'warning' ? 'notifications_active' : 'monitoring'} 
                size={16} 
                decorative 
              />
              <span style={{ fontSize: tokens.font.size.sm, fontWeight: tokens.font.weight.medium }}>
                Current usage
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
            }}>
              <div style={{ 
                position: 'relative',
                width: tokens.layout.popoverSm * 0.7 + 'px',
                height: tokens.space[2] + 'px',
                background: 'var(--md-surface-variant)',
                borderRadius: tokens.radius.sm,
                overflow: 'hidden'
              }}>
                {/* 80% notch marker */}
                <div style={{
                  position: 'absolute',
                  left: '80%',
                  top: `${-tokens.space[0] - 2}px`,
                  width: `${tokens.space[0] + 2}px`,
                  height: `${tokens.space[3]}px`,
                  background: 'var(--md-outline)',
                  borderRadius: tokens.radius.sm,
                  opacity: 0.7,
                }} />
                
                {/* Progress fill */}
                <div style={{
                  width: `${Math.min(100, percentage)}%`,
                  height: '100%',
                  background: severity === 'warning' ? 'var(--md-warning)' : 'var(--md-primary)',
                  borderRadius: tokens.radius.sm,
                }} />
              </div>
              <span style={{ 
                fontSize: tokens.font.size.xs,
                minWidth: tokens.layout.popoverSm * 0.35 + 'px',
              }}>
                {used} of {total} used ({Math.round(percentage)}%)
              </span>
            </div>
            
            <div style={{
              marginTop: tokens.space[1],
              fontSize: tokens.font.size.xs,
              opacity: 0.8,
            }}>
              Budget window: {timeWindow}
            </div>
          </div>

          {/* Why info */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: tokens.space[2],
            padding: tokens.space[2],
            background: tokens.color.surface[1],
            borderRadius: tokens.radius.input,
            marginBottom: tokens.space[4],
          }}>
            <Icon name="info" size={16} decorative />
            <div style={{
              fontSize: tokens.font.size.xs,
              color: tokens.color.ink.secondary,
            }}>
              This confirmation appears for expensive operations to help you track usage and costs.
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: tokens.space[2],
          justifyContent: 'flex-end',
          padding: `${tokens.space[2]} ${tokens.space[4]} ${tokens.space[4]}`,
          borderTop: tokens.border.thin,
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: tokens.density.touchMin,
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              background: 'transparent',
              color: tokens.color.ink.primary,
              paddingInline: tokens.space[4],
              cursor: 'pointer',
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.medium,
            }}
          >
            <Icon name="close" size={16} decorative style={{ marginRight: tokens.space[1] }} />
            Cancel
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            style={{
              minHeight: tokens.density.touchMin,
              border: 'none',
              borderRadius: tokens.radius.md,
              background: 'var(--md-primary)',
              color: 'var(--md-on-primary)',
              paddingInline: tokens.space[4],
              cursor: 'pointer',
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.medium,
            }}
          >
            <Icon name="check" size={16} decorative style={{ marginRight: tokens.space[1] }} />
            Confirm and proceed
          </button>
        </div>
      </div>
    </>
  );
}